// src/pages/VerifyEmailPage.jsx
import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { authService } from '../services/auth';

const VerifyEmailPage = () => {
  const { refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const prefillEmail = params.get('email') || '';
  const prefillCode = params.get('code') || '';

  const isReactivation = location.pathname.includes('/reactivate');

  const [email, setEmail] = useState(prefillEmail);
  const [code, setCode] = useState(prefillCode);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setEmail(prefillEmail);
    setCode(prefillCode);
  }, [prefillEmail, prefillCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email');
      return;
    }
    if (!code || !/^\d{6}$/.test(code)) {
      setError('Please enter the 6-digit code');
      return;
    }

    setLoading(true);

    try {
      const res = isReactivation
        ? await authService.reactivate({ email, code })
        : await authService.verifyEmail({ email, code });

      if (res.token) {
        // token returned: user is logged in
        try { await refreshUser(); } catch (err) { /* ignore */ }
        navigate('/dashboard');
        return;
      }

      if (res.status === 'success') {
        setMessage(res.message || 'Verified successfully.');
        try { await refreshUser(); } catch (err) { /* ignore */ }
        // Small delay then redirect to login or dashboard
        setTimeout(() => navigate('/login'), 1200);
        return;
      }

      setError(res.message || res.error || 'Verification failed');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Verification failed');
    }

    setLoading(false);
  };

  const handleResend = async () => {
    setError('');
    setMessage('');
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email to resend code');
      return;
    }
    setLoading(true);
    try {
      const res = await authService.resendVerification({ email });
      if (res.status === 'success') {
        setMessage('Verification code sent. Check your email.');
      } else {
        setError(res.message || 'Unable to resend code');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to resend code');
    }
    setLoading(false);
  };

  const handleRequestReactivation = async () => {
    setError('');
    setMessage('');
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email');
      return;
    }
    setLoading(true);
    try {
      const res = await authService.requestReactivation({ email });
      if (res.status === 'success') {
        setMessage('Reactivation code sent to your email');
      } else {
        setError(res.message || 'Unable to send reactivation code');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to send reactivation code');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-gray-900">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900 dark:text-white">
            {isReactivation ? 'Reactivate your account' : 'Verify your email'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            {isReactivation ? 'Enter the 6-digit code we sent to your email to reactivate.' : 'Enter the 6-digit code we sent to your email to verify your email address.'}
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow-xl rounded-lg sm:px-10">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
                  <div className="flex">
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800 dark:text-red-300">{error}</h3>
                    </div>
                  </div>
                </div>
              )}

              {message && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4">
                  <div className="flex">
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-green-800 dark:text-green-300">{message}</h3>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email address</label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none block w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 dark:text-gray-300">6-digit code</label>
                <div className="mt-1">
                  <input
                    id="code"
                    name="code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                    className="appearance-none block w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                    placeholder="123456"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex justify-center py-3 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  {isReactivation ? 'Reactivate account' : 'Verify email'}
                </button>
              </div>

              <div className="flex items-center justify-between">
                {!isReactivation && (
                  <button type="button" onClick={handleResend} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">Resend code</button>
                )}
                {isReactivation && (
                  <button type="button" onClick={handleRequestReactivation} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">Request reactivation code</button>
                )}
                <Link to="/login" className="text-sm text-gray-600 dark:text-gray-300">Back to sign in</Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
